<template>
  <div class="w-full border-b border-neutral-secondary bg-neutral-primary">
    <div class="px-3 pt-1 pb-2">
      <table class="w-full">
        <colgroup>
          <col class="w-[10%]" />
          <col />
          <col />
          <col class="w-[10%]" />
        </colgroup>
        <thead class="text-neutral-secondary">
          <tr>
            <th class="p-1">
              <div class="flex items-center">
                <NText variant="caption2">
                  {{ t('misc.quantity') }}
                </NText>
              </div>
            </th>
            <th class="p-1">
              <div class="flex justify-end">
                <NText variant="caption2" class="align-bottom">
                  {{ t('symbol.volume') }}
                </NText>
                <NText
                  variant="caption2"
                  class="align-bottom min-w-[66px] text-left"
                >
                  {{ t('common.price') }}
                </NText>
              </div>
            </th>
            <th class="p-1">
              <div class="flex items-center">
                <NText
                  variant="caption2"
                  class="align-bottom min-w-[66px] text-right"
                >
                  {{ t('common.price') }}
                </NText>
                <NText variant="caption2" class="align-bottom">
                  {{ t('symbol.volume') }}
                </NText>
              </div>
            </th>
            <th class="p-1">
              <div class="flex justify-end items-center">
                <NText variant="caption2" class="align-bottom">
                  {{ t('misc.quantity') }}
                </NText>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) of items" :key="item.rowPlace">
            <td class="text-right pt-0.5">
              <NumberFormatter #="{ number }" :value="item.askNumber">
                <NText
                  tag="div"
                  :variant="variant"
                  :class="{
                    'text-neutral-tertiary-disable': isDisabled(item.askPrice),
                  }"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </td>
            <td class="pr-1 pt-0.5 relative h-8">
              <div class="bg-transparent h-full" dir="ltr">
                <div
                  v-if="!isDisabled(item.askPrice)"
                  class="bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 h-full rounded-r-sm transition-[width] ease-in-out duration-1000"
                  :style="{ width: `${progressPercentage(item.askQuantity)}%` }"
                />
                <div
                  class="absolute inset-0 top-0.5 flex items-center text-left px-1"
                >
                  <NPopper hover placement="top">
                    <span class="min-w-[66px]">
                      <NumberFormatter #="{ number }" :value="item.askPrice">
                        <NText
                          v-gid="{
                            module: 'order',
                            screen: 'home',
                            section: 'symbolDetailBestLimit',
                            component: 'priceSellButton',
                          }"
                          :variant="variant"
                          :weight="fontWeight"
                          class="cursor-pointer text-brand-primary"
                          :class="{
                            '!text-neutral-tertiary-disable pointer-events-none':
                              isDisabled(item.askPrice),
                          }"
                          @click="
                            handleClickPrice(item.askPrice, ORDER_TYPES.sell)
                          "
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </span>

                    <template #content>
                      <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                        <NumberFormatter
                          #="{ number }"
                          :value="
                            calcReferencePriceDifference({
                              price: item.askPrice,
                            })
                          "
                          maximum-fraction-digits="2"
                          minimum-fraction-digits="2"
                          number-style="percent"
                          sign-display="exceptZero"
                        >
                          <NText
                            variant="caption2"
                            weight="medium"
                            class="text-neutral-quaternary"
                            dir="ltr"
                            :class="{
                              '!text-brand-secondary':
                                calcReferencePriceDifference({
                                  price: item.askPrice,
                                }) > 0,
                              '!text-critical-secondary':
                                calcReferencePriceDifference({
                                  price: item.askPrice,
                                }) < 0,
                            }"
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                      </div>
                    </template>
                  </NPopper>

                  <NPopper hover placement="top">
                    <span>
                      <NumberFormatter
                        #="{ number }"
                        :notation="showUnit ? 'compact' : 'standard'"
                        :notation-limit="volumeNotationLimit"
                        :value="item.askQuantity"
                      >
                        <NText
                          v-gid="{
                            module: 'order',
                            screen: 'home',
                            section: 'symbolDetailBestLimit',
                            component: 'quantitySellButton',
                          }"
                          :variant="variant"
                          :weight="fontWeight"
                          class="cursor-pointer"
                          :class="{
                            'text-neutral-tertiary-disable pointer-events-none':
                              isDisabled(item.askPrice),
                          }"
                          @click="
                            handleClickQuantity(
                              index,
                              'askQuantity',
                              ORDER_TYPES.sell,
                              item.askPrice
                            )
                          "
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </span>
                    <template #content>
                      <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                        <NumberFormatter
                          #="{ number }"
                          :value="calcAskVolume(index)"
                        >
                          <NText
                            variant="caption2"
                            weight="medium"
                            class="text-neutral-quaternary"
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                      </div>
                    </template>
                  </NPopper>
                </div>
              </div>
            </td>
            <td class="pl-1 pt-0.5 relative h-8">
              <div class="bg-transparent h-full">
                <div
                  v-if="!isDisabled(item.bidPrice)"
                  class="bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 h-full rounded-l-sm transition-[width] ease-in-out duration-1000"
                  :style="{ width: `${progressPercentage(item.bidQuantity)}%` }"
                ></div>
                <div class="absolute inset-0 top-0.5 flex items-center px-1">
                  <NPopper hover placement="top">
                    <span class="min-w-[66px] text-right">
                      <NumberFormatter #="{ number }" :value="item.bidPrice">
                        <NText
                          v-gid="{
                            module: 'order',
                            screen: 'home',
                            section: 'symbolDetailBestLimit',
                            component: 'priceBuyButton',
                          }"
                          :variant="variant"
                          :weight="fontWeight"
                          class="cursor-pointer text-critical-primary"
                          :class="{
                            '!text-neutral-tertiary-disable pointer-events-none':
                              isDisabled(item.bidPrice),
                          }"
                          @click="
                            handleClickPrice(item.bidPrice, ORDER_TYPES.buy)
                          "
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </span>

                    <template #content>
                      <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                        <NumberFormatter
                          #="{ number }"
                          :value="
                            calcReferencePriceDifference({
                              price: item.bidPrice,
                            })
                          "
                          maximum-fraction-digits="2"
                          minimum-fraction-digits="2"
                          number-style="percent"
                          sign-display="exceptZero"
                        >
                          <NText
                            variant="caption2"
                            weight="medium"
                            class="text-neutral-quaternary"
                            dir="ltr"
                            :class="{
                              '!text-brand-secondary':
                                calcReferencePriceDifference({
                                  price: item.bidPrice,
                                }) > 0,
                              '!text-critical-secondary':
                                calcReferencePriceDifference({
                                  price: item.bidPrice,
                                }) < 0,
                            }"
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                      </div>
                    </template>
                  </NPopper>

                  <NPopper hover placement="top">
                    <span>
                      <NumberFormatter
                        #="{ number }"
                        :notation="showUnit ? 'compact' : 'standard'"
                        :notation-limit="volumeNotationLimit"
                        :value="item.bidQuantity"
                      >
                        <NText
                          v-gid="{
                            module: 'order',
                            screen: 'home',
                            section: 'symbolDetailBestLimit',
                            component: 'quantityBuyButton',
                          }"
                          :variant="variant"
                          :weight="fontWeight"
                          class="cursor-pointer"
                          :class="{
                            'text-neutral-tertiary-disable pointer-events-none':
                              isDisabled(item.bidPrice),
                          }"
                          @click="
                            handleClickQuantity(
                              index,
                              'bidQuantity',
                              ORDER_TYPES.buy,
                              item.bidPrice
                            )
                          "
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </span>

                    <template #content>
                      <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                        <NumberFormatter
                          #="{ number }"
                          :value="calcBidVolume(index)"
                        >
                          <NText
                            variant="caption2"
                            weight="medium"
                            class="text-neutral-quaternary"
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                      </div>
                    </template>
                  </NPopper>
                </div>
              </div>
            </td>
            <td class="text-left pt-0.5">
              <NumberFormatter #="{ number }" :value="item.bidNumber">
                <NText
                  tag="div"
                  :variant="variant"
                  :class="{
                    'text-neutral-tertiary-disable': isDisabled(item.bidPrice),
                  }"
                >
                  {{ number }}
                </NText>
              </NumberFormatter>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { BidAsk } from '@libs/api-reference';
import { ORDER_TYPES } from '@core/order';

import { useLiveSymbolQuery } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';

type SymbolBidAskTableProps = {
  items: BidAsk[];
  showTitle?: boolean;
  isin: string;
  lowerPriceThreshold: number;
  upperPriceThreshold: number;
  size: string;
  updateOrderFormData?: boolean;
  showUnit?: boolean;
  volumeNotationLimit?: string;
  isOption?: boolean;
};

type SymbolBidAskEmits = {
  (
    e: 'updateOrder',
    { quantity, price }: { quantity?: number; price?: number }
  ): void;
};

const props = withDefaults(defineProps<SymbolBidAskTableProps>(), {
  showTitle: true,
  updateOrderFormData: false,
  showUnit: true,
  volumeNotationLimit: '999999999',
  isOption: false,
});

const emit = defineEmits<SymbolBidAskEmits>();

const { openNewOrderFormDialog } = useOrderDialogs();

const { symbol } = useLiveSymbolQuery(toRef(props, 'isin'), {
  immediate: false,
});

const variant = computed(() =>
  props.size === 'small' ? 'overline' : 'caption2'
);

const fontWeight = computed(() => (props.size === 'small' ? 'medium' : 'bold'));

const { t } = useI18n();

const isDisabled = (item) => {
  return (
    !props.isOption &&
    (item < props.lowerPriceThreshold || item > props.upperPriceThreshold)
  );
};

const handleClickQuantity = (index, property, type, price) => {
  let quantity = 0;
  for (let i = 0; i <= index; i++) {
    quantity += props.items[i][property];
  }

  if (props.updateOrderFormData) {
    emit('updateOrder', { quantity, price });
  } else {
    openNewOrderFormDialog({
      symbol: {
        isin: symbol.value.isin as string,
        instrumentName: symbol.value.instrumentName as string,
        instrumentTypeCode: symbol.value.instrumentTypeCode as string,
      },
      quantity,
      type,
      price,
    });
  }
};

const handleClickPrice = (price, type) => {
  if (props.updateOrderFormData) {
    emit('updateOrder', { price });
  } else {
    openNewOrderFormDialog({
      price,
      type,
      symbol: {
        isin: symbol.value.isin as string,
        instrumentName: symbol.value.instrumentName as string,
        instrumentTypeCode: symbol.value.instrumentTypeCode as string,
      },
    });
  }
};

const activeAsk = computed(() => {
  return props.items.filter((x) => {
    if (!isDisabled(x.askPrice)) {
      return x.askQuantity;
    }
  });
});

const activeBid = computed(() => {
  return props.items.filter((x) => {
    if (!isDisabled(x.bidPrice)) {
      return x.bidQuantity;
    }
  });
});

const maxQuantity = computed(() => {
  const maxAskQuantity = Math.max.apply(
    Math,
    activeAsk.value.map((o) => {
      return o.askQuantity;
    })
  );

  const maxBidQuantity = Math.max.apply(
    Math,
    activeBid.value.map((o) => {
      return o.bidQuantity;
    })
  );

  return Math.max(maxBidQuantity, maxAskQuantity);
});

const progressPercentage = (item) => {
  return (item / maxQuantity.value) * 100;
};

const nonDisabledAskPrice = computed(() =>
  props.items.filter((item) => !isDisabled(item.askPrice))
);
const nonDisabledBidPrice = computed(() =>
  props.items.filter((item) => !isDisabled(item.bidPrice))
);

const calcAskVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledAskPrice.value[i];

    if (item) {
      quantity += item.askQuantity;
    }
  }

  return quantity;
};

const calcBidVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledBidPrice.value[i];

    if (item) {
      quantity += item.bidQuantity;
    }
  }

  return quantity;
};

const calcReferencePriceDifference = ({ price }: { price: number }) => {
  return symbol.value.referencePrice
    ? (price - symbol.value?.referencePrice) / symbol.value?.referencePrice
    : 0;
};
</script>
